'use client';

import { useEffect, useState } from 'react';
import { useRealtimeRun } from '@trigger.dev/react-hooks';
import { WarningCircle, Download } from '@phosphor-icons/react';
import { RemotionRenderer } from '@/components/visualizations/remotion-renderer';
import { LiveRemotionRenderer } from '@/components/visualizations/live-remotion-renderer';
import { RenderJobCard } from '@/components/render-job-card';
import { emitAutoSystemMessage } from '@/lib/auto-system-message-bus';
import { narratedRunToView, type RenderJobView } from '@/lib/render-job-view';

/** Realtime statuses where the run has reached a terminal state on its own. */
const REALTIME_TERMINAL = new Set([
  'COMPLETED', 'FAILED', 'CANCELED', 'CRASHED', 'SYSTEM_FAILURE', 'TIMED_OUT', 'INTERRUPTED', 'EXPIRED',
]);
/** Realtime statuses that read as "still queued" from the card's perspective. */
const REALTIME_QUEUED = new Set(['QUEUED', 'PENDING_VERSION', 'DELAYED']);

interface PendingNarratedVideoProps {
  taskId: string;
  publicAccessToken: string;
  title: string;
  estimatedDurationSec?: number;
}

/**
 * Live progress card that subscribes to a trigger.dev run and renders the final
 * video card in place once the task completes. Replaces the chat tool's pending
 * output ({ taskId, publicAccessToken }) until the actual video is ready.
 *
 * The in-progress / failed chrome is the shared RenderJobCard consuming the
 * `narratedRunToView` contract adapter (docs/RENDER_STATUS_CONTRACT.md). The
 * completed-state renderers and the auto-system-message emission below are
 * agent-facing behavior and stay exactly as they were.
 */
export function PendingNarratedVideo({
  taskId,
  publicAccessToken,
  title,
  estimatedDurationSec,
}: PendingNarratedVideoProps) {
  const { run, error } = useRealtimeRun(taskId, {
    accessToken: publicAccessToken,
  });

  // Self-heal: if a run expires in the queue before a worker attaches AND the
  // realtime token lapses around the same time, the subscription never observes
  // the terminal transition and this card would show "Queued for rendering…"
  // forever (the live incident). When the realtime view is stuck queued (or the
  // subscription errored) we poll the server's reconciled status — which asks
  // the trigger API what actually happened. A reconciled FAILED renders the
  // honest failure; a reconciled COMPLETED (the run actually finished while the
  // socket was dead) swaps the card to the finished artifact instead of merely
  // unfreezing.
  const [reconciled, setReconciled] = useState<
    | { status: 'failed'; error?: string }
    | { status: 'completed'; outputUrl?: string; visualizationId?: string }
    | null
  >(null);
  const realtimeTerminal = run?.status ? REALTIME_TERMINAL.has(run.status) : false;
  const looksStuckQueued = !run || (run.status ? REALTIME_QUEUED.has(run.status) : false) || !!error;
  useEffect(() => {
    if (reconciled || realtimeTerminal || !looksStuckQueued) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const res = await fetch(`/api/render/narrated/${encodeURIComponent(taskId)}/status`);
        if (res.ok) {
          const data = (await res.json()) as {
            status?: string;
            error?: string;
            outputUrl?: string;
            visualizationId?: string;
          };
          if (!cancelled && data.status === 'failed') {
            setReconciled({ status: 'failed', error: data.error });
            return; // resolved — stop polling
          }
          if (!cancelled && data.status === 'completed') {
            setReconciled({
              status: 'completed',
              outputUrl: data.outputUrl,
              visualizationId: data.visualizationId,
            });
            return; // resolved — stop polling
          }
        }
      } catch {
        // transient — retry on the next tick
      }
      if (!cancelled) timer = setTimeout(poll, 20_000);
    };
    // Delay the first check so the realtime subscription gets first crack.
    timer = setTimeout(poll, 8_000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [taskId, reconciled, realtimeTerminal, looksStuckQueued]);

  // Emit an auto-system-message when the reconciled path (not realtime)
  // resolves a run, so the agent still learns about completion/failure even
  // though the live subscription was dead. Bus dedups by eventId.
  useEffect(() => {
    if (reconciled?.status === 'completed') {
      emitAutoSystemMessage({
        eventId: `narrated-video-reconciled-complete:${taskId}`,
        kind: 'render-complete',
        payload: {
          taskId,
          ...(reconciled.visualizationId ? { visualizationId: reconciled.visualizationId } : {}),
          ...(reconciled.outputUrl ? { videoUrl: reconciled.outputUrl } : {}),
        },
        body: `Narrated video render finished (recovered after the live connection dropped).${reconciled.outputUrl ? ` The MP4 is ready at ${reconciled.outputUrl}.` : ''}${reconciled.visualizationId ? ` It is saved as visualization ${reconciled.visualizationId}.` : ''}`,
      });
    }
    if (reconciled?.status === 'failed') {
      emitAutoSystemMessage({
        eventId: `narrated-video-reconciled-failed:${taskId}`,
        kind: 'render-error',
        payload: { taskId, ...(reconciled.error ? { error: reconciled.error } : {}) },
        body: `Narrated video render did not finish: ${reconciled.error ?? 'the run died in the queue before a worker picked it up'}. Acknowledge it didn't complete and offer to retry.`,
      });
    }
  }, [reconciled, taskId]);

  // Emit auto-system-messages on terminal transitions so the agent learns
  // about render completion (or failure) and can react in a fresh turn —
  // e.g. acknowledge the video, or offer to retry on failure. Bus dedups
  // by eventId, so re-renders are safe.
  const runStatus = run?.status;
  const runOutput = run?.output as { visualizationId?: string; videoUrl?: string; renderError?: string } | undefined;
  useEffect(() => {
    if (runStatus === 'COMPLETED' && runOutput?.visualizationId) {
      emitAutoSystemMessage({
        eventId: `narrated-video-complete:${taskId}`,
        kind: runOutput.renderError ? 'render-error' : 'render-complete',
        payload: {
          taskId,
          visualizationId: runOutput.visualizationId,
          ...(runOutput.videoUrl ? { videoUrl: runOutput.videoUrl } : {}),
          ...(runOutput.renderError ? { error: runOutput.renderError } : {}),
        },
        body: runOutput.renderError
          ? `Narrated video render finished but the Lambda MP4 export failed: ${runOutput.renderError}. The in-browser player still works; offer to retry the export or move on.`
          : `Narrated video render finished. The video is ready and rendered in the side panel; you can reference it as visualization ${runOutput.visualizationId}.`,
      });
    }
    if (runStatus && ['FAILED', 'CANCELED', 'CRASHED', 'SYSTEM_FAILURE', 'TIMED_OUT', 'INTERRUPTED', 'EXPIRED'].includes(runStatus)) {
      emitAutoSystemMessage({
        eventId: `narrated-video-failed:${taskId}`,
        kind: 'render-error',
        payload: {
          taskId,
          status: runStatus,
        },
        body: `Narrated video render task ended in ${runStatus.toLowerCase().replace('_', ' ')}. Acknowledge to the user that the video didn't finish and offer to retry with a smaller scope or different settings.`,
      });
    }
  }, [runStatus, runOutput?.visualizationId, runOutput?.videoUrl, runOutput?.renderError, taskId]);

  // Task completed → render the final video card directly from the output.
  // The orchestrator now ALWAYS saves componentCode (even on Lambda failure),
  // so this branch fires for both full success and partial success.
  if (run?.status === 'COMPLETED' && run.output) {
    const output = run.output as {
      visualizationId?: string;
      videoUrl?: string;
      componentCode?: string;
      durationSec?: number;
      renderError?: string;
    };
    if (output.componentCode) {
      const fps = 30;
      const totalFrames = Math.max(1, Math.round((output.durationSec ?? 4) * fps));
      return (
        <div className="my-3">
          {output.visualizationId ? (
            // Saved video → use the live wrapper so the model's surgical
            // edits via `editVisualization` re-render the player in place
            // without the user having to reload the page.
            <LiveRemotionRenderer
              title={title}
              initialComponentCode={output.componentCode}
              durationInFrames={totalFrames}
              fps={fps}
              width={1280}
              height={720}
              visualizationId={output.visualizationId}
              displayContext="inline"
            />
          ) : (
            // No visualizationId means we couldn't save server-side; fall
            // back to the static renderer (model can't edit it anyway).
            <RemotionRenderer
              title={title}
              componentCode={output.componentCode}
              durationInFrames={totalFrames}
              fps={fps}
              width={1280}
              height={720}
              displayContext="inline"
            />
          )}
          {output.videoUrl ? (
            <div className="mt-2 flex items-center gap-2">
              <a
                href={output.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-300 hover:text-violet-200"
              >
                <Download weight="bold" className="size-3.5" />
                Direct MP4
              </a>
            </div>
          ) : output.renderError ? (
            // Lambda failed but the video plays in-browser. The RemotionRenderer
            // above already exposes an "Export to MP4" button — clicking it
            // retries Lambda. So we just show a friendly note here.
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90">
              <WarningCircle weight="duotone" className="size-4 shrink-0 mt-0.5 text-amber-400" />
              <div>
                <p className="font-medium">MP4 export didn't finish — your video plays here in the browser.</p>
                <p className="text-amber-200/70 mt-0.5">Click <span className="font-medium">Export to MP4</span> on the player above to retry the download.</p>
              </div>
            </div>
          ) : null}
        </div>
      );
    }
  }

  // Server reconciliation resolved a run whose realtime subscription was stuck
  // (died in the queue, or finished after the socket dropped). Render the honest
  // terminal state instead of a perpetual "Queued…" shimmer — a completed run
  // swaps to the finished artifact (the MP4 outputUrl), a failed one to the
  // failure card.
  if (reconciled) {
    const reconciledView: RenderJobView =
      reconciled.status === 'completed'
        ? {
            id: taskId,
            kind: 'narrated',
            status: 'completed',
            progress: 1,
            outputUrl: reconciled.outputUrl,
            canCancel: false,
            canRetry: false,
            meta: estimatedDurationSec ? { videoDurationSec: estimatedDurationSec } : undefined,
          }
        : {
            id: taskId,
            kind: 'narrated',
            status: 'failed',
            error: reconciled.error ?? 'The render run did not finish.',
            canCancel: false,
            canRetry: false,
            meta: estimatedDurationSec ? { videoDurationSec: estimatedDurationSec } : undefined,
          };
    return (
      <div className="my-3">
        <RenderJobCard view={reconciledView} title={title || 'Composing your video'} />
      </div>
    );
  }

  // Everything else — queued, executing, failed, canceled, or a subscription
  // error — renders through the shared contract card. The adapter owns the
  // status mapping and stage wording; the card owns the chrome.
  const view = narratedRunToView(taskId, run, {
    subscribeError: error ?? undefined,
    videoDurationSec: estimatedDurationSec,
  });

  return (
    <div className="my-3">
      <RenderJobCard view={view} title={title || 'Composing your video'} />
    </div>
  );
}
